import React, { useEffect, useRef, useState } from "react";
import { VISIBLE_TITLES } from "../data/titles";

const clipVariants = ["lr-clip-a", "lr-clip-b", "lr-clip-c"];

// A single portfolio card. The poster sits in its own 9:16 frame with the
// hover video / "coming this summer" overlay contained inside it. The genre
// and title live *below* the poster so they never obstruct the artwork.
const TitleCard = ({ t, index, activeId, setActiveId }) => {
  const variant = clipVariants[index % clipVariants.length];
  const teasers = Array.isArray(t.teasers) ? t.teasers : [];
  const hasTeasers = teasers.length > 0;

  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const cursorRef = useRef(0); // sequential cursor into `teasers`
  const [chosenIdx, setChosenIdx] = useState(null);
  const [mounted, setMounted] = useState(false);
  const isTouchRef = useRef(false);

  const active = activeId === t.id;

  // Detect touch — on mobile, cards auto-activate via intersection instead of
  // hover. We only wire the observer for cards that have teasers.
  useEffect(() => {
    const isTouch =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(hover: none)").matches;
    isTouchRef.current = isTouch;
    if (!isTouch || !hasTeasers) return undefined;
    const el = cardRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.7) {
            const next = cursorRef.current % teasers.length;
            setChosenIdx(next);
            cursorRef.current = next + 1;
            setMounted(true);
            setActiveId(t.id);
          } else if (activeId === t.id) {
            setActiveId(null);
          }
        });
      },
      { threshold: [0, 0.35, 0.7, 1] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasTeasers, teasers.length, t.id, activeId, setActiveId]);

  // Play / pause. Each activation advances to the next teaser in sequence.
  useEffect(() => {
    if (!hasTeasers) return;
    const v = videoRef.current;
    if (!v) return;
    if (active) {
      try {
        v.currentTime = 0;
      } catch (_e) {
        /* ignore */
      }
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      v.pause();
      try {
        v.currentTime = 0;
      } catch (_e) {
        /* ignore */
      }
    }
  }, [active, hasTeasers, chosenIdx]);

  const activate = () => {
    if (!hasTeasers) {
      setActiveId(t.id);
      return;
    }
    const next = cursorRef.current % teasers.length;
    setChosenIdx(next);
    cursorRef.current = next + 1;
    setMounted(true);
    setActiveId(t.id);
  };

  const deactivate = () => {
    if (activeId === t.id) setActiveId(null);
  };

  const onTap = () => {
    if (!isTouchRef.current) return;
    if (active) deactivate();
    else activate();
  };

  const chosenSrc =
    hasTeasers && chosenIdx !== null && chosenIdx < teasers.length
      ? teasers[chosenIdx]
      : null;

  return (
    <div
      ref={cardRef}
      className="shrink-0 snap-start flex flex-col"
      style={{ width: "clamp(220px, 24vw, 320px)" }}
      data-testid={`title-card-${t.id}`}
      onMouseEnter={activate}
      onMouseLeave={deactivate}
      onFocus={activate}
      onBlur={deactivate}
      onClick={onTap}
    >
      {/* POSTER FRAME — 9:16, contains only artwork / video / "coming soon"
          overlay. Title & genre are rendered BELOW the frame. */}
      <div
        className="relative overflow-hidden"
        style={{
          aspectRatio: "9 / 16",
          background: "#0d0d0d",
          borderRadius: "18px",
        }}
      >
        <div
          className={`absolute inset-0 ${variant}`}
          style={{
            willChange: "transform",
            opacity: active && hasTeasers ? 0 : 1,
            transition: "opacity 500ms cubic-bezier(0.22, 0.61, 0.36, 1)",
          }}
          data-testid={`title-poster-${t.id}`}
        >
          <img
            src={t.poster}
            alt=""
            aria-hidden
            draggable={false}
            className="w-full h-full object-cover"
            style={{ filter: "saturate(0.95) contrast(1.04)" }}
          />
        </div>

        {hasTeasers && mounted && chosenSrc && (
          <video
            key={chosenSrc}
            ref={videoRef}
            src={chosenSrc}
            muted
            loop
            playsInline
            preload="none"
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            draggable={false}
            aria-hidden
            data-testid={`title-teaser-${t.id}`}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              opacity: active ? 1 : 0,
              transition: "opacity 500ms cubic-bezier(0.22, 0.61, 0.36, 1)",
            }}
          />
        )}

        {/* Cards with no teaser: a subtle "coming this summer" reveal, kept
            inside the poster area so it never touches the title/genre below. */}
        {!hasTeasers && (
          <div
            aria-hidden
            className="absolute inset-0 flex items-center justify-center pointer-events-none px-5"
            style={{
              background: "rgba(8, 8, 8, 0.72)",
              opacity: active ? 1 : 0,
              transition: "opacity 500ms cubic-bezier(0.22, 0.61, 0.36, 1)",
            }}
            data-testid={`title-comingsoon-${t.id}`}
          >
            <div
              className="text-center text-sm md:text-base tracking-[0.18em] uppercase"
              style={{
                color: "#F5F5F3",
                fontWeight: 500,
                lineHeight: 1.5,
                letterSpacing: "0.18em",
              }}
            >
              Coming to Screens<br />This Summer
            </div>
          </div>
        )}
      </div>

      {/* GENRE + TITLE — under the poster, clean and unobstructed. */}
      <div className="mt-4 md:mt-5 px-1">
        <div
          className="text-[10px] tracking-[0.32em] uppercase mb-2"
          style={{ color: "#A1A1AA", fontWeight: 500 }}
          data-testid={`title-genre-${t.id}`}
        >
          {t.genre}
        </div>
        <div
          className="text-lg md:text-xl"
          style={{
            color: "#F5F5F3",
            letterSpacing: "-0.02em",
            fontWeight: 500,
            lineHeight: 1.1,
          }}
          data-testid={`title-name-${t.id}`}
        >
          {t.title}
        </div>
      </div>
    </div>
  );
};

// A deliberate portfolio-shaped announcement card. Sits at the end of the
// carousel — same 9:16 frame, no photographic image, refined typography.
const ComingSoonCard = () => {
  return (
    <div
      className="shrink-0 snap-start flex flex-col"
      style={{ width: "clamp(220px, 24vw, 320px)" }}
      data-testid="title-card-coming-soon"
    >
      <div
        className="relative overflow-hidden flex items-center justify-center"
        style={{
          aspectRatio: "9 / 16",
          borderRadius: "18px",
          background:
            "radial-gradient(ellipse 80% 60% at 50% 30%, rgba(255,106,61,0.08) 0%, rgba(255,106,61,0) 60%), linear-gradient(180deg, #0f0f0f 0%, #080808 100%)",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        {/* Fine grid texture — subtle, dry-brushed feel. */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            opacity: 0.5,
          }}
        />
        <div className="relative text-center px-5">
          <div
            className="leading-[1.1]"
            style={{
              fontSize: "clamp(1.35rem, 2.1vw, 1.85rem)",
              letterSpacing: "-0.025em",
              fontWeight: 500,
              color: "#F5F5F3",
            }}
          >
            More projects.
            <br />
            To be announced soon.
          </div>
          <div
            className="mt-6 text-[10px] tracking-[0.24em] uppercase"
            style={{ color: "#FF6A3D", fontWeight: 500 }}
          >
            New slates in development
          </div>
        </div>
      </div>

      <div className="mt-4 md:mt-5 px-1">
        <div
          className="text-[10px] tracking-[0.32em] uppercase mb-2"
          style={{ color: "#A1A1AA", fontWeight: 500 }}
        >
          &mdash;
        </div>
        <div
          className="text-lg md:text-xl"
          style={{
            color: "#F5F5F3",
            opacity: 0.75,
            letterSpacing: "-0.02em",
            fontWeight: 500,
            lineHeight: 1.1,
          }}
        >
          Coming Soon
        </div>
      </div>
    </div>
  );
};

export const SelectedTitles = () => {
  const [activeId, setActiveId] = useState(null);
  const scrollerRef = useRef(null);
  const dragStateRef = useRef({
    dragging: false,
    startX: 0,
    startScroll: 0,
    moved: false,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateEdges = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft < max - 4);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateEdges();
    el.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    return () => {
      el.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
    };
  }, []);

  // NOTE: we intentionally do NOT convert vertical wheel input into horizontal
  // carousel scroll. Vertical page scroll must always work; the carousel is
  // navigated via the arrow buttons, drag-to-scroll (mouse), and native
  // horizontal gestures (trackpad / touch).

  // Drag-to-scroll for mouse users only.
  const onMouseDown = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    const canHover =
      window.matchMedia && window.matchMedia("(hover: hover)").matches;
    if (!canHover) return;
    dragStateRef.current.dragging = true;
    dragStateRef.current.moved = false;
    dragStateRef.current.startX = e.pageX;
    dragStateRef.current.startScroll = el.scrollLeft;
  };
  const onMouseMove = (e) => {
    const s = dragStateRef.current;
    if (!s.dragging) return;
    const el = scrollerRef.current;
    if (!el) return;
    const dx = e.pageX - s.startX;
    if (Math.abs(dx) > 4) s.moved = true;
    el.scrollLeft = s.startScroll - dx;
  };
  const endDrag = () => {
    dragStateRef.current.dragging = false;
  };
  const onClickCapture = (e) => {
    if (dragStateRef.current.moved) {
      e.stopPropagation();
      e.preventDefault();
      dragStateRef.current.moved = false;
    }
  };

  const scrollByCards = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardW =
      el.querySelector("[data-testid^='title-card-']")?.getBoundingClientRect()
        .width || 280;
    const gap = 24;
    el.scrollBy({ left: dir * (cardW + gap) * 1.5, behavior: "smooth" });
  };

  return (
    <section
      id="titles"
      data-testid="titles-section"
      className="relative w-full pt-24 md:pt-36 pb-24 md:pb-32"
      style={{ background: "rgba(8, 8, 8, 0.88)" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <h2
              className="leading-[0.95]"
              style={{
                fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)",
                letterSpacing: "-0.04em",
                fontWeight: 500,
                color: "#F5F5F3",
              }}
              data-testid="titles-headline"
            >
              Selected Work.
            </h2>
            <div
              className="mt-5 md:mt-6 text-[11px] md:text-xs uppercase"
              style={{
                color: "#A1A1AA",
                letterSpacing: "0.24em",
                fontWeight: 300,
              }}
              data-testid="titles-categories"
            >
              Scripted&nbsp;&nbsp;/&nbsp;&nbsp;Vertical Series&nbsp;&nbsp;/&nbsp;&nbsp;Entertainment&nbsp;&nbsp;/&nbsp;&nbsp;Generative
            </div>
            <p
              className="mt-8 md:mt-10 leading-[1.6]"
              style={{
                fontSize: "clamp(1rem, 1.3vw, 1.15rem)",
                letterSpacing: "-0.005em",
                color: "#F5F5F3",
                opacity: 0.82,
                fontWeight: 400,
                maxWidth: "620px",
              }}
              data-testid="titles-subhead"
            >
              We produce scripted series, international formats, mobile-first
              entertainment and generative content through scalable production
              units built around each project.
            </p>
            <p
              className="mt-5 leading-[1.6]"
              style={{
                fontSize: "clamp(1rem, 1.3vw, 1.15rem)",
                letterSpacing: "-0.005em",
                color: "#F5F5F3",
                opacity: 0.7,
                fontWeight: 400,
                maxWidth: "620px",
              }}
              data-testid="titles-subhead-2"
            >
              We made our name in vertical drama &mdash; a format where a story
              has only seconds to earn attention. Today, we bring that same
              instinct for strong hooks, emotional clarity and immediate
              audience engagement across every format and every screen.
            </p>
          </div>

          <div
            className="flex flex-col md:items-end gap-4"
            data-testid="production-credit"
          >
            <span
              className="text-[10px] tracking-[0.32em] uppercase"
              style={{ color: "#A1A1AA", fontWeight: 500 }}
            >
              <span style={{ color: "#FF6A3D" }}>—</span>&nbsp;&nbsp;Trusted by
            </span>
            <div className="flex items-center gap-10 md:gap-12">
              <img
                src="https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/whnsvtz9_holywater_tech_max2000.png"
                alt="Holywater"
                draggable={false}
                data-testid="logo-holywater"
                style={{
                  height: "48px",
                  width: "auto",
                  filter: "brightness(1.1) contrast(1.15)",
                  opacity: 0.95,
                }}
              />
              <img
                src="https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/egyv5m6h_My%20Drama%20logo%201%20%281%29.png"
                alt="MyDrama"
                draggable={false}
                data-testid="logo-mydrama"
                style={{
                  height: "44px",
                  width: "auto",
                  opacity: 0.95,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="relative">
        {/* Edge fades */}
        <div
          aria-hidden
          className="absolute top-0 bottom-0 left-0 pointer-events-none"
          style={{
            width: "80px",
            background:
              "linear-gradient(to right, rgba(8,8,8,0.9), rgba(8,8,8,0))",
            opacity: canPrev ? 1 : 0,
            transition: "opacity 300ms ease",
            zIndex: 5,
          }}
        />
        <div
          aria-hidden
          className="absolute top-0 bottom-0 right-0 pointer-events-none"
          style={{
            width: "80px",
            background:
              "linear-gradient(to left, rgba(8,8,8,0.9), rgba(8,8,8,0))",
            opacity: canNext ? 1 : 0,
            transition: "opacity 300ms ease",
            zIndex: 5,
          }}
        />

        {/* Arrow controls */}
        <button
          type="button"
          onClick={() => scrollByCards(-1)}
          aria-label="Previous titles"
          data-testid="titles-prev"
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 items-center justify-center"
          style={{
            width: 48,
            height: 48,
            borderRadius: "999px",
            background: "rgba(15,15,15,0.75)",
            border: "1px solid #1f1f1f",
            color: "#F5F5F3",
            opacity: canPrev ? 1 : 0,
            pointerEvents: canPrev ? "auto" : "none",
            transition: "opacity 300ms ease, background 200ms ease",
            zIndex: 6,
            cursor: "pointer",
          }}
        >
          <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>
            ‹
          </span>
        </button>
        <button
          type="button"
          onClick={() => scrollByCards(1)}
          aria-label="Next titles"
          data-testid="titles-next"
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 items-center justify-center"
          style={{
            width: 48,
            height: 48,
            borderRadius: "999px",
            background: "rgba(15,15,15,0.75)",
            border: "1px solid #1f1f1f",
            color: "#F5F5F3",
            opacity: canNext ? 1 : 0,
            pointerEvents: canNext ? "auto" : "none",
            transition: "opacity 300ms ease, background 200ms ease",
            zIndex: 6,
            cursor: "pointer",
          }}
        >
          <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>
            ›
          </span>
        </button>

        <div
          ref={scrollerRef}
          className="w-full overflow-x-auto pb-4 no-scrollbar snap-x"
          style={{
            scrollbarWidth: "none",
            WebkitOverflowScrolling: "touch",
            cursor: "grab",
          }}
          data-testid="titles-scroller"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onClickCapture={onClickCapture}
        >
          <div
            className="flex gap-4 md:gap-6 px-6 md:px-12 items-start"
            style={{ width: "max-content" }}
          >
            {VISIBLE_TITLES.map((t, i) => (
              <TitleCard
                key={t.id}
                t={t}
                index={i}
                activeId={activeId}
                setActiveId={setActiveId}
              />
            ))}
            <ComingSoonCard />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SelectedTitles;
